import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { renderAsync } from "docx-preview";
import { toast } from "react-toastify";

type ContratoViewerProps = {
  open: boolean;
  onClose: () => void;
  link: string;
  titulo?: string;
};

type FileType = "pdf" | "docx-office" | "docx-fallback";

export const ContratoViewer: React.FC<ContratoViewerProps> = ({ open, onClose, link, titulo }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [fileType, setFileType] = useState<FileType | null>(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [officeViewerUrl, setOfficeViewerUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !link) return;

    setFileType(null);
    setPdfBlobUrl(null);
    setOfficeViewerUrl(null);

    const lowerLink = link.split("?")[0].toLowerCase();

    // PDF direto: exibe sem download.
    if (lowerLink.endsWith(".pdf")) {
      setFileType("pdf");
      setPdfBlobUrl(link);
      return;
    }

    // DOCX (.docx ou .doc): usa o Office Web Viewer da Microsoft. Renderiza fielmente
    // imagens floating, fontes, layout — superior à docx-preview no navegador.
    // Requer que o `link` (presigned URL do MinIO) seja acessível pela internet.
    if (lowerLink.endsWith(".docx") || lowerLink.endsWith(".doc")) {
      const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(link)}`;
      setOfficeViewerUrl(viewerUrl);
      setFileType("docx-office");
      return;
    }

    // Link sem extensão reconhecível: faz fetch para detectar PDF vs DOCX pelo magic number.
    let cancelled = false;
    let createdUrl: string | null = null;

    (async () => {
      try {
        const res = await fetch(link);
        const contentType = res.headers.get("content-type") || "";
        const buffer = await res.arrayBuffer();

        const arr = new Uint8Array(buffer).subarray(0, 4);
        const header = arr.reduce((acc, byte) => acc + String.fromCharCode(byte), "");

        if (header === "%PDF" || contentType.includes("pdf")) {
          const blob = new Blob([buffer], { type: "application/pdf" });
          const url = window.URL.createObjectURL(blob);
          createdUrl = url;
          if (!cancelled) {
            setPdfBlobUrl(url);
            setFileType("pdf");
          }
          return;
        }

        // Sem extensão e não é PDF: assume DOCX e tenta Office Viewer.
        // Se o link não for público a tentativa falha visualmente, mas é raro chegar aqui.
        const viewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(link)}`;
        if (!cancelled) {
          setOfficeViewerUrl(viewerUrl);
          setFileType("docx-office");
        }
      } catch (err) {
        console.error("Erro ao carregar documento:", err);
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) window.URL.revokeObjectURL(createdUrl);
    };
  }, [open, link]);

  // Fallback manual (botão): se o Office Viewer não funcionar (link privado, MS fora do ar),
  // o usuário pode forçar o uso da docx-preview tradicional.
  const usarFallbackDocxPreview = async () => {
    if (!link) return;
    try {
      const res = await fetch(link);
      const buffer = await res.arrayBuffer();
      setFileType("docx-fallback");
      // O DOM do iframe some quando trocamos fileType; o useEffect abaixo cuida do render.
      setTimeout(() => {
        if (containerRef.current) {
          containerRef.current.innerHTML = "";
          renderAsync(buffer, containerRef.current);
        }
      }, 0);
    } catch (err) {
      console.error("Erro no fallback docx-preview:", err);
    }
  };

  const handleDownload = () => {
    if (!link) return;
    const a = document.createElement("a");
    a.href = link;
    a.download = "";
    a.click();
  };

  // Imprime o .docx numa janela própria, só com o documento renderizado pela docx-preview. O Office
  // Viewer roda num iframe de outro domínio: window.print() imprimia a tela do sistema (menu, modal)
  // com o contrato cortado no tamanho do iframe, por isso a impressão saía desconfigurada.
  const imprimirDocx = async () => {
    // Abre antes de qualquer await, senão o navegador trata como pop-up e bloqueia.
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    try {
      const res = await fetch(link);
      // Link expirado ou arquivo indisponível: sem isto a janela fechava sem dizer nada.
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const buffer = await res.arrayBuffer();
      const container = document.createElement("div");
      await renderAsync(buffer, container, undefined, { breakPages: true, useBase64URL: true });

      // Monta a janela pelo DOM (sem document.write): o estilo de impressão no head e o documento no body.
      const doc = printWindow.document;
      doc.title = titulo ?? "Contrato";
      const estilo = doc.createElement("style");
      estilo.textContent = `
        @page { size: A4; margin: 0; }
        html, body { margin: 0; padding: 0; background: white; }
        .docx-wrapper { background: none !important; padding: 0 !important; display: block !important; }
        .docx-wrapper > section.docx { box-shadow: none !important; margin: 0 auto !important; break-after: page; }
        .docx-wrapper > section.docx:last-child { break-after: auto; }
      `;
      doc.head.appendChild(estilo);
      doc.body.innerHTML = container.innerHTML;

      // Espera imagens e fontes: imprimir antes deixa buracos no documento.
      await Promise.all(Array.from(doc.images).map((img) =>
        img.complete ? null : new Promise((resolve) => { img.onload = img.onerror = resolve; })));
      await (doc as any).fonts?.ready;
      printWindow.focus();
      printWindow.print();
    } catch (err) {
      console.error("Erro ao preparar a impressão do contrato:", err);
      printWindow.close();
      toast.error("Não foi possível preparar o contrato para impressão. Feche e abra o contrato de novo e tente imprimir.");
    }
  };

  const handlePrint = () => {
    if (fileType === "pdf" && pdfBlobUrl) {
      const printWindow = window.open(pdfBlobUrl, "_blank");
      if (printWindow) printWindow.print();
    } else if (fileType === "docx-office" || fileType === "docx-fallback") {
      imprimirDocx();
    }
  };

  if (!open) return null;

  return (
    <Overlay>
      <Modal>
        <Header>
          <Title>{titulo ?? "Contrato"}</Title>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </Header>

        <Content
          ref={containerRef}
          style={{ display: fileType === "docx-fallback" ? "block" : "none" }}
        />

        {fileType === "pdf" && pdfBlobUrl && (
          <Content>
            <iframe
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="PDF Viewer"
            />
          </Content>
        )}

        {fileType === "docx-office" && officeViewerUrl && (
          <Content>
            <iframe
              src={officeViewerUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="DOCX Viewer"
            />
          </Content>
        )}

        <Footer>
          <Button onClick={onClose}>Fechar</Button>
          <Button onClick={handleDownload}>Baixar</Button>
          <Button onClick={handlePrint}>Imprimir</Button>
        </Footer>
      </Modal>
    </Overlay>
  );
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  width: 85%;
  height: 90%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #ddd;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.2rem;
`;

const CloseButton = styled.button`
  font-size: 1.5rem;
  border: none;
  background: transparent;
  cursor: pointer;
`;

const Content = styled.div`
  flex: 1;
  overflow: auto;
  padding: 16px;
  background: #f9f9f9;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #ddd;
`;

const Button = styled.button`
  padding: 10px 16px;
  border-radius: 8px;
  border: none;
  background: #1a3a68;
  color: white;
  cursor: pointer;
  font-size: 0.95rem;
  font-weight: 500;
  transition: background 0.2s ease;

  &:hover {
    background: #142b4d;
  }
`;